import { useTranslation } from 'react-i18next';

import {
    Body1,
    Flex,
    HorizontalDivider,
    IconInfo,
    OutlinedCard,
    Stack,
    Subtitle2,
    Subtitle3,
    TextButton,
    Timeline,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import {
    type PriorAuthorizationInfoFragment,
    PriorAuthorizationStatus,
} from '../../../graphql/generated/graphql';

import { generateSteps } from './generate-steps';
import { PriorAuthInfoCover } from './PriorAuthInfoCover';

export const PriorAuthTimeline = ({
    priorAuthInfo,
}: {
    priorAuthInfo: PriorAuthorizationInfoFragment;
}) => {
    const { t, i18n } = useTranslation();
    const [opened, { open, close }] = useDisclosure(false);

    const isCancelledStatus =
        priorAuthInfo.status === PriorAuthorizationStatus.Cancelled;
    const cancelledSubtitle = isCancelledStatus ? (
        <Subtitle3 color="error">
            {t('priorAuthorization.timelineCard.PaCancelled')}
        </Subtitle3>
    ) : null;

    const { steps, activeStepIndex } = generateSteps(
        priorAuthInfo,
        t,
        cancelledSubtitle,
        i18n.language
    );

    const timelineSteps = steps.map((step, index) => {
        const isFutureStep = index > activeStepIndex;

        return {
            ...step,
            inactive: isCancelledStatus && isFutureStep,
        };
    });

    const timelineColor = isCancelledStatus ? 'error' : 'info';
    const shouldShowDescription =
        priorAuthInfo.status === PriorAuthorizationStatus.New ||
        priorAuthInfo.status === PriorAuthorizationStatus.Received ||
        priorAuthInfo.status === PriorAuthorizationStatus.InReview;

    return (
        <OutlinedCard>
            <Stack gap="xs">
                <Flex justify="space-between">
                    <Subtitle2>
                        {t('priorAuthorization.timelineCard.heading')}
                    </Subtitle2>
                    <TextButton onClick={open} LeftIcon={IconInfo}>
                        {t('priorAuthorization.timelineCard.textButton')}
                    </TextButton>
                </Flex>
                <HorizontalDivider />

                <Timeline
                    color={timelineColor}
                    steps={timelineSteps}
                    activeStep={activeStepIndex}
                />

                {shouldShowDescription ? (
                    <>
                        <HorizontalDivider />
                        <Body1>
                            {t('priorAuthorization.timelineCard.description')}
                        </Body1>
                    </>
                ) : null}
            </Stack>
            <PriorAuthInfoCover isOpen={opened} onClose={close} />
        </OutlinedCard>
    );
};
